import React, { useState, useRef, useEffect } from "react";
import {
  FaPrint, FaTimes, FaCheckCircle, FaCar, FaFileInvoiceDollar,
  FaShieldAlt, FaIdCard, FaBuilding, FaUserCheck, FaLock, FaDownload,
  FaEdit, FaPlus, FaTrashAlt
} from "react-icons/fa";

const DEFAULT_FI_PRODUCTS = [
  {
    product_id: "FI004",
    _id: "FI004",
    name: "Guaranteed Asset Protection (GAP)",
    short_name: "GAP Protection",
    price: 995,
    description: "Waives loan deficiency balance & pays up to $1,000 deductible if vehicle totaled/stolen."
  },
  {
    product_id: "FI001",
    _id: "FI001",
    name: "Vehicle Service Contract (VSC)",
    short_name: "VSC / Warranty",
    price: 3100,
    description: "100% parts & labor mechanical & electrical repair coverage nationwide with $0 deductible."
  },
  {
    product_id: "FI002",
    _id: "FI002",
    name: "Prepaid Maintenance Care",
    short_name: "Prepaid Maintenance",
    price: 1200,
    description: "Factory synthetic oil, filters, tire rotations, brake checks & multipoint inspections."
  },
  {
    product_id: "FI003",
    _id: "FI003",
    name: "Platinum Plus Appearance & Wheel",
    short_name: "Platinum Plus Appearance",
    price: 1295,
    description: "Alloy wheel curb rash, road hazard tire blowout repair, windshield & paintless dent care."
  }
];

const DealPrintReport = ({
  isOpen,
  onClose,
  deal,
  activeCar = {},
  customerName: propCustomerName = "",
  customerInfo = {},
  uploadedDocs = {},
  salespersonId = "",
  dealershipData = {},
  paymentDetails = {},
  salePrice: propSalePrice,
  docFee: propDocFee,
  registrationFee: propRegFee,
  taxRate: propTaxRate,
  salesTax: propSalesTax,
  accessories: propAccessories,
  tradeAllowance: propTradeAllowance,
  tradePayoff: propTradePayoff,
  tradeEquity: propTradeEquity,
  downPayment: propDownPayment,
  customerRebate: propCustomerRebate,
  selectedProducts: propSelectedProducts,
  productTotal: propProductTotal,
  totalAmountDue: propTotalAmountDue,
  totalAmountFinanced: propTotalFinanced,
  paymentPreference: propPaymentPreference,
  apr: propApr,
  termMonths: propTermMonths,
  monthlyPayment: propMonthlyPayment
}) => {
  const reportRef = useRef(null);

  // 1. Vehicle & Core Extraction
  const vehicle = deal?.vehicle || activeCar || {};
  const finance = deal?.finance || {};
  const trade = deal?.trade || deal?.tradeIn || {};
  const signature = deal?.signature || {};
  const approval = deal?.approvalDetails || {};

  // 2. Editable Dealership Title & Subtitle State (Point 2)
  const [dealershipTitle, setDealershipTitle] = useState(
    dealershipData?.name || deal?.dealershipName || deal?.dealership?.name || "POHANKA AUTOMOTIVE GROUP"
  );
  const [dealershipSubtitle, setDealershipSubtitle] = useState(
    "AUTHORIZED DEALERSHIP · CUSTOMER SALES AGREEMENT · BUYER'S ORDER RECAPITULATION"
  );
  const [logoUrl, setLogoUrl] = useState(
    dealershipData?.logoUrl || deal?.dealership?.logoUrl || "/logo512.png"
  );

  // Helper to format clean human sales officer name (never raw hexadecimal Mongo ID strings like 6766caae...)
  const formatCleanSalespersonName = (val) => {
    if (!val) return "Bobby Bonser";
    const str = String(val).trim();
    if (/^[0-9a-fA-F]{24}$/.test(str) || str.includes("6766caae") || str.includes("Dealership") || str.includes("Admin") || str.includes("KIOSK")) {
      return "Bobby Bonser";
    }
    return str.replace(/\s*\([^)]*\)/g, "").trim();
  };

  // 3. Editable Sales Consultant Name State (Point 3)
  const initialConsultant = formatCleanSalespersonName(
    approval?.approvedBy ||
    (typeof salespersonId === "object" ? (salespersonId?.name || salespersonId?.firstName) : salespersonId) ||
    deal?.salespersonName ||
    deal?.salesperson?.name ||
    "Bobby Bonser"
  );
  const [salesConsultantName, setSalesConsultantName] = useState(initialConsultant);

  // Sync states if props change
  useEffect(() => {
    if (dealershipData?.name || deal?.dealershipName || deal?.dealership?.name) {
      setDealershipTitle(dealershipData?.name || deal?.dealershipName || deal?.dealership?.name || "POHANKA AUTOMOTIVE GROUP");
    }
    if (dealershipData?.logoUrl || deal?.dealership?.logoUrl) {
      setLogoUrl(dealershipData?.logoUrl || deal?.dealership?.logoUrl || "/logo512.png");
    }
    const currentSp = formatCleanSalespersonName(
      approval?.approvedBy ||
      (typeof salespersonId === "object" ? (salespersonId?.name || salespersonId?.firstName) : salespersonId) ||
      deal?.salespersonName ||
      deal?.salesperson?.name ||
      "Bobby Bonser"
    );
    setSalesConsultantName(currentSp);
  }, [dealershipData, deal, salespersonId, approval]);

  // 4. Customer Information
  const customerName = propCustomerName || customerInfo?.name || `${customerInfo?.firstName || ''} ${customerInfo?.lastName || ''}`.trim() || deal?.customer?.name || deal?.customerInfo?.name || "Valued Customer";
  const customerPhone = customerInfo?.phone || deal?.customer?.phone || deal?.customerInfo?.phone || "(555) 234-5678";
  const customerEmail = customerInfo?.email || deal?.customer?.email || deal?.customerInfo?.email || "customer@example.com";
  const customerAddress = customerInfo?.address || deal?.customer?.address || deal?.customerInfo?.address || "123 Main Street";
  const customerCity = customerInfo?.city || deal?.customer?.city || deal?.customerInfo?.city || "Salisbury";
  const customerState = customerInfo?.state || deal?.customer?.state || deal?.customerInfo?.state || "MD";
  const customerZip = customerInfo?.zip || deal?.customer?.zip || deal?.customerInfo?.zip || "21801";

  // 5. Protection Plans (F&I Products) Management (Point 6)
  const initialProducts = (() => {
    if (propSelectedProducts && propSelectedProducts.length > 0) return propSelectedProducts;
    if (paymentDetails?.selectedProducts && paymentDetails.selectedProducts.length > 0) return paymentDetails.selectedProducts;
    if (deal?.fAndI?.products && deal.fAndI.products.length > 0) {
      if (typeof deal.fAndI.products[0] === "object") return deal.fAndI.products;
      return DEFAULT_FI_PRODUCTS.filter(p => deal.fAndI.products.includes(p.product_id || p._id));
    }
    return DEFAULT_FI_PRODUCTS; // Default: include standard protection package
  })();

  const [activeProducts, setActiveProducts] = useState(initialProducts);

  useEffect(() => {
    if (propSelectedProducts && propSelectedProducts.length > 0) {
      setActiveProducts(propSelectedProducts);
    } else if (paymentDetails?.selectedProducts && paymentDetails.selectedProducts.length > 0) {
      setActiveProducts(paymentDetails.selectedProducts);
    } else if (deal?.fAndI?.products && deal.fAndI.products.length > 0) {
      if (typeof deal.fAndI.products[0] === "object") setActiveProducts(deal.fAndI.products);
      else setActiveProducts(DEFAULT_FI_PRODUCTS.filter(p => deal.fAndI.products.includes(p.product_id || p._id)));
    }
  }, [propSelectedProducts, paymentDetails?.selectedProducts, deal?.fAndI?.products]);

  const toggleProduct = (prod) => {
    const pId = prod.product_id || prod._id || prod.productId;
    const exists = activeProducts.some(p => (p.product_id || p._id || p.productId) === pId);
    if (exists) {
      setActiveProducts(prev => prev.filter(p => (p.product_id || p._id || p.productId) !== pId));
    } else {
      setActiveProducts(prev => [...prev, prod]);
    }
  };

  const productTotal = activeProducts.reduce((sum, p) => sum + Number(p.price || 0), 0);

  // 6. Base Calculations & Fees
  const salePrice = Number(propSalePrice || vehicle.salePrice || vehicle.sale_price || vehicle.msrp || 33999);
  const accessories = Number(propAccessories || vehicle.accessories || 0);
  const docFee = Number(propDocFee !== undefined ? propDocFee : (vehicle.docFee || dealershipData.doc_fee || 800));
  const regFee = Number(propRegFee !== undefined ? propRegFee : (vehicle.registration || dealershipData.registration_fee || 772));
  const taxRate = Number(propTaxRate !== undefined ? propTaxRate : (dealershipData.tax_rate || 0.07));
  const salesTax = Number(propSalesTax !== undefined ? propSalesTax : parseFloat((salePrice * taxRate).toFixed(2)));
  const totalAmountDue = parseFloat((salePrice + accessories + docFee + regFee + salesTax).toFixed(2));

  const hasTrade = trade?.hasTrade || (Number(trade.allowance || propTradeAllowance || 0) > 0);
  const tradeAllowance = hasTrade ? Number(trade.allowance || propTradeAllowance || 0) : 0;
  const tradePayoff = hasTrade ? Number(trade.payoff || propTradePayoff || 0) : 0;
  const tradeEquity = Number(propTradeEquity !== undefined ? propTradeEquity : (trade.equity !== undefined ? trade.equity : (paymentDetails.tradeEquity || (tradeAllowance - tradePayoff))));

  const downPayment = Number(propDownPayment !== undefined ? propDownPayment : (finance.downPayment || paymentDetails.downPayment || 0));
  const customerRebate = Number(propCustomerRebate !== undefined ? propCustomerRebate : (paymentDetails.customerRebate || 0));

  const paymentPreference = propPaymentPreference || paymentDetails.paymentPreference || deal?.finance?.paymentType || "FINANCE";
  const apr = Number(propApr !== undefined ? propApr : (finance.apr || paymentDetails.apr || 5.99));
  const termMonths = Number(propTermMonths !== undefined ? propTermMonths : (finance.term || paymentDetails.term || 72));

  // TOTAL AMOUNT FINANCED (including itemized protection products)
  const totalFinanced = parseFloat(Math.max(0, totalAmountDue + productTotal - tradeEquity - downPayment - customerRebate).toFixed(2));
  const monthlyPayment = Number(
    propMonthlyPayment !== undefined
      ? propMonthlyPayment
      : paymentDetails.monthlyPayment || (totalFinanced > 0 ? (totalFinanced * (apr / 1200)) / (1 - Math.pow(1 + (apr / 1200), -termMonths)) : 0)
  );

  const handleSavePdf = () => {
    try {
      const reportElement = reportRef.current;
      if (!reportElement) {
        window.print();
        return;
      }

      // Clone element to ensure updated input values are set as clean text
      const clone = reportElement.cloneNode(true);

      // Sync input values from the real DOM to the cloned DOM
      const originalInputs = reportElement.querySelectorAll("input, select, textarea");
      const clonedInputs = clone.querySelectorAll("input, select, textarea");
      originalInputs.forEach((input, idx) => {
        if (clonedInputs[idx]) {
          const span = document.createElement("span");
          span.className = clonedInputs[idx].className;
          span.textContent = input.value;
          clonedInputs[idx].parentNode.replaceChild(span, clonedInputs[idx]);
        }
      });

      // Remove print:hidden elements from clone
      clone.querySelectorAll(".print\\:hidden, button").forEach(el => el.remove());

      const iframe = document.createElement("iframe");
      iframe.style.position = "fixed";
      iframe.style.right = "0";
      iframe.style.bottom = "0";
      iframe.style.width = "0";
      iframe.style.height = "0";
      iframe.style.border = "0";
      document.body.appendChild(iframe);

      // Collect all existing stylesheets
      let stylesHtml = "";
      document.querySelectorAll("link[rel='stylesheet'], style").forEach(node => {
        stylesHtml += node.outerHTML;
      });

      const doc = iframe.contentWindow.document;
      doc.open();
      doc.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Automotive Sales Agreement - ${dealershipTitle || "Pohanka"}</title>
            <meta charset="utf-8" />
            <meta name="viewport" content="width=device-width, initial-scale=1" />
            <link href="https://cdn.jsdelivr.net/npm/tailwindcss@2.2.19/dist/tailwind.min.css" rel="stylesheet">
            ${stylesHtml}
            <style>
              @page {
                size: letter portrait;
                margin: 5mm 6mm;
              }
              * {
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
                box-sizing: border-box !important;
              }
              html, body {
                background: #ffffff !important;
                color: #0f172a !important;
                font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif !important;
                margin: 0 !important;
                padding: 0 !important;
                width: 100% !important;
              }
              #deal-print-document {
                max-width: 100% !important;
                width: 100% !important;
                margin: 0 auto !important;
                padding: 4px 6px !important;
                box-shadow: none !important;
                border: none !important;
                background: #ffffff !important;
              }
            </style>
          </head>
          <body class="bg-white text-slate-900 p-0 m-0">
            ${clone.outerHTML}
          </body>
        </html>
      `);
      doc.close();

      setTimeout(() => {
        try {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
        } catch (err) {
          console.error("Iframe print error, falling back to window.print:", err);
          window.print();
        } finally {
          setTimeout(() => {
            if (iframe.parentNode) {
              iframe.parentNode.removeChild(iframe);
            }
          }, 3000);
        }
      }, 350);
    } catch (e) {
      console.warn("Direct window.print fallback:", e);
      window.print();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[99999] bg-black/90 backdrop-blur-md flex items-center justify-center p-2 sm:p-4 overflow-y-auto print:p-0 print:bg-white print:static print:inset-auto">
      <div className="fixed top-3 right-3 sm:top-4 sm:right-4 z-[100000] flex items-center gap-2 sm:gap-3 print:hidden">
        <button
          type="button"
          onClick={handleSavePdf}
          className="bg-gradient-to-r from-emerald-400 via-teal-400 to-cyan-400 text-slate-950 font-black px-4 sm:px-6 py-2 sm:py-2.5 rounded-2xl text-[11px] sm:text-xs uppercase tracking-wider shadow-xl hover:scale-105 active:scale-95 transition-all flex items-center gap-2 cursor-pointer"
        >
          <FaDownload size={13} /> Save as PDF
        </button>
        <button
          type="button"
          onClick={onClose}
          className="bg-[#121a2f] hover:bg-slate-800 text-slate-300 hover:text-white border border-slate-700 px-3.5 sm:px-4 py-2 sm:py-2.5 rounded-2xl text-xs font-bold transition-colors cursor-pointer flex items-center gap-1.5"
        >
          <FaTimes size={13} /> Close
        </button>
      </div>

      <div
        id="deal-print-document"
        ref={reportRef}
        className="bg-white text-slate-900 w-full max-w-4xl rounded-2xl shadow-2xl p-4 sm:p-7 md:p-9 space-y-4 sm:space-y-5 my-auto font-sans print:shadow-none print:m-0 print:p-2 print:max-w-none print:w-full print:rounded-none"
        style={{ colorScheme: "light", fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif" }}
      >
        <div className="border-b-2 border-slate-900 pb-3 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div className="space-y-1 flex-1">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-start flex-shrink-0 max-h-12 max-w-[200px]">
                <img
                  src={logoUrl || "/logo512.png"}
                  alt={dealershipTitle}
                  loading="eager"
                  decoding="async"
                  onError={(e) => { e.currentTarget.src = "/favicon.png"; }}
                  className="h-10 sm:h-12 w-auto object-contain max-w-[180px]"
                />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 group">
                  <input
                    type="text"
                    value={dealershipTitle}
                    onChange={(e) => setDealershipTitle(e.target.value)}
                    className="text-base sm:text-lg md:text-xl font-black uppercase tracking-tight text-slate-950 bg-transparent border-b border-transparent hover:border-slate-300 focus:border-cyan-500 focus:outline-none w-full cursor-text truncate font-sans"
                    placeholder="Enter Dealership Name (e.g. POHANKA AUTOMOTIVE GROUP)"
                  />
                  <FaEdit className="text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 print:hidden text-xs" />
                </div>
                <input
                  type="text"
                  value={dealershipSubtitle}
                  onChange={(e) => setDealershipSubtitle(e.target.value)}
                  className="text-[8.5px] sm:text-[9.5px] text-slate-500 font-semibold tracking-wider uppercase bg-transparent border-b border-transparent hover:border-slate-300 focus:border-cyan-500 focus:outline-none w-full font-sans truncate"
                />
              </div>
            </div>
          </div>
          <div className="text-left sm:text-right space-y-0.5 border-t sm:border-t-0 pt-2 sm:pt-0 border-slate-200 w-full sm:w-auto flex-shrink-0 font-sans">
            <span className="inline-block bg-slate-900 text-white text-[9px] sm:text-[10px] font-black px-2.5 py-0.5 rounded tracking-widest uppercase shadow-sm">
              DEAL #{deal?.dealNumber || "D493896"}
            </span>
            <p className="text-[9px] sm:text-[10px] text-slate-600 font-bold">
              Date: {new Date().toLocaleDateString("en-US", { year: 'numeric', month: 'short', day: 'numeric' })} · {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </p>
            <div className="text-[9px] sm:text-[10px] text-slate-600 flex items-center sm:justify-end gap-1 group">
              <span className="text-slate-500">Sales Consultant:</span>
              <input
                type="text"
                value={salesConsultantName}
                onChange={(e) => setSalesConsultantName(e.target.value)}
                className="font-bold text-slate-950 underline decoration-slate-400 bg-transparent border-b border-transparent hover:border-slate-400 focus:border-cyan-500 focus:outline-none text-[9.5px] max-w-[180px] font-sans"
                placeholder="Sales Consultant Name"
              />
              <FaEdit className="text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity print:hidden text-[9px]" />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs font-sans">
          <div className="border border-slate-300 rounded-xl p-3 space-y-2 bg-slate-50/60 shadow-sm">
            <span className="text-[8.5px] sm:text-[9px] font-black uppercase tracking-wider text-slate-500 block border-b border-slate-200 pb-1">
              1. BUYER INFORMATION
            </span>
            <div className="grid grid-cols-2 gap-2 text-slate-700">
              <div>
                <span className="text-[8px] text-slate-400 uppercase font-bold block">Customer Full Name</span>
                <strong className="text-slate-950 text-[10.5px] sm:text-[11px] block">{customerName}</strong>
              </div>
              <div>
                <span className="text-[8px] text-slate-400 uppercase font-bold block">Primary Phone</span>
                <span className="text-slate-900 font-medium text-[10.5px] sm:text-[11px] block">{customerPhone}</span>
              </div>
              <div className="col-span-2">
                <span className="text-[8px] text-slate-400 uppercase font-bold block">Email Address</span>
                <span className="text-slate-900 font-medium text-[10.5px] sm:text-[11px] block">{customerEmail}</span>
              </div>
              <div className="col-span-2">
                <span className="text-[8px] text-slate-400 uppercase font-bold block">Billing &amp; Residence Address</span>
                <span className="text-slate-900 font-medium text-[10.5px] sm:text-[11px] block">{customerAddress}, {customerCity}, {customerState} {customerZip}</span>
              </div>
            </div>
          </div>
          <div className="border border-slate-300 rounded-xl p-3 space-y-2 bg-slate-50/60 shadow-sm">
            <span className="text-[8.5px] sm:text-[9px] font-black uppercase tracking-wider text-slate-500 block border-b border-slate-200 pb-1">
              2. VEHICLE PURCHASED
            </span>
            <div className="grid grid-cols-2 gap-2 text-slate-700">
              <div>
                <span className="text-[8px] text-slate-400 uppercase font-bold block">Year / Make / Model</span>
                <strong className="text-slate-950 text-[10.5px] sm:text-[11px] block">{vehicle.year || 2026} {vehicle.make || 'Toyota'} {vehicle.model || 'Camry'}</strong>
              </div>
              <div>
                <span className="text-[8px] text-slate-400 uppercase font-bold block">Trim &amp; Finish</span>
                <span className="text-slate-900 font-medium text-[10.5px] sm:text-[11px] block">{vehicle.trim || 'XSE'} · {vehicle.color || 'Midnight Black'}</span>
              </div>
              <div>
                <span className="text-[8px] text-slate-400 uppercase font-bold block">Stock Number</span>
                <span className="font-bold text-slate-950 text-[10.5px] sm:text-[11px] block">{vehicle.stockNumber || vehicle.stock_number || 'TY26001'}</span>
              </div>
              <div>
                <span className="text-[8px] text-slate-400 uppercase font-bold block">Odometer / Condition</span>
                <span className="text-slate-900 font-medium text-[10.5px] sm:text-[11px] block">{vehicle.mileage ? `${Number(vehicle.mileage).toLocaleString()} mi` : 'Brand New (8 mi)'}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="border border-slate-300 rounded-xl overflow-hidden shadow-sm font-sans">
          <div className="bg-slate-900 text-white px-3.5 py-1.5 flex justify-between items-center">
            <span className="text-[9px] sm:text-[10px] font-black uppercase tracking-widest flex items-center gap-1.5 font-sans">
              <FaFileInvoiceDollar className="text-cyan-400" /> 3. FINANCIAL RECAPITULATION &amp; SETTLEMENT BREAKDOWN
            </span>
            <span className="text-[8.5px] sm:text-[9px] font-bold text-slate-300 uppercase font-sans">Payment Mode: {paymentPreference}</span>
          </div>
          <table className="w-full text-left border-collapse font-sans">
            <tbody className="divide-y divide-slate-200 text-[10.5px] sm:text-[11px]">
              <tr className="bg-white">
                <td className="py-1.5 px-3.5 text-slate-700 font-medium font-sans">Agreed Vehicle Purchase Price (MSRP / Sale Price)</td>
                <td className="py-1.5 px-3.5 text-right font-bold text-slate-900 font-sans">${salePrice.toLocaleString()}</td>
              </tr>
              {accessories > 0 && (
                <tr className="bg-slate-50/60">
                  <td className="py-1.5 px-3.5 text-slate-700 font-medium font-sans">Installed Factory Accessories &amp; Equipment</td>
                  <td className="py-1.5 px-3.5 text-right font-bold text-slate-900 font-sans">${accessories.toLocaleString()}</td>
                </tr>
              )}
              <tr className="bg-slate-50/60">
                <td className="py-1.5 px-3.5 text-slate-700 font-medium font-sans">Dealer Processing &amp; Documentation Fee</td>
                <td className="py-1.5 px-3.5 text-right font-bold text-slate-900 font-sans">${docFee.toLocaleString()}</td>
              </tr>
              <tr className="bg-white">
                <td className="py-1.5 px-3.5 text-slate-700 font-medium font-sans">State Motor Vehicle Title &amp; Registration Fee</td>
                <td className="py-1.5 px-3.5 text-right font-bold text-slate-900 font-sans">${regFee.toLocaleString()}</td>
              </tr>
              <tr className="bg-slate-50/60">
                <td className="py-1.5 px-3.5 text-slate-700 font-medium font-sans">State Sales Tax ({(taxRate * 100).toFixed(1)}%)</td>
                <td className="py-1.5 px-3.5 text-right font-bold text-slate-900 font-sans">${salesTax.toLocaleString()}</td>
              </tr>
              {activeProducts.length > 0 && (
                <>
                  <tr className="bg-cyan-50/70 border-t border-cyan-200">
                    <td colSpan={2} className="py-1 px-3.5 text-[8.5px] sm:text-[9px] font-black uppercase tracking-wider text-cyan-900 font-sans">
                      🛡️ Dealership Protection Package ({activeProducts.length} Plans Included)
                    </td>
                  </tr>
                  {activeProducts.map((p, idx) => (
                    <tr key={idx} className="bg-cyan-50/40 text-cyan-950 text-[10px] sm:text-[10.5px]">
                      <td className="py-1 px-3.5 pl-6 text-slate-700 flex items-center justify-between font-sans">
                        <div>
                          <span className="font-bold text-slate-900">• {p.name || p.short_name}</span>
                          {p.description && <span className="text-[8.5px] text-slate-500 block">{p.description}</span>}
                        </div>
                      </td>
                      <td className="py-1 px-3.5 text-right font-bold text-cyan-950 font-sans align-top">
                        +${Number(p.price || 0).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </>
              )}
              <tr className="bg-slate-100 font-bold border-t border-slate-300 text-[10.5px] sm:text-[11px]">
                <td className="py-1.5 px-3.5 text-slate-900 font-sans">Total Purchase Amount Due (Vehicle, Fees, Tax &amp; Protection)</td>
                <td className="py-1.5 px-3.5 text-right text-slate-950 font-sans font-bold">${(totalAmountDue + productTotal).toLocaleString()}</td>
              </tr>
              {hasTrade && (
                <tr className="bg-emerald-50/60 text-emerald-900 font-semibold text-[10.5px] sm:text-[11px]">
                  <td className="py-1.5 px-3.5 font-sans">
                    Trade-in Equity Credit ({trade.year || 2021} {trade.model || 'Trade Vehicle'} · Allowance: ${tradeAllowance.toLocaleString()}, Payoff: ${tradePayoff.toLocaleString()})
                  </td>
                  <td className="py-1.5 px-3.5 text-right text-emerald-700 font-sans font-bold">-${Math.abs(tradeEquity).toLocaleString()}</td>
                </tr>
              )}
              {downPayment > 0 && (
                <tr className="bg-emerald-50/60 text-emerald-900 font-semibold text-[10.5px] sm:text-[11px]">
                  <td className="py-1.5 px-3.5 font-sans">Customer Cash Down Payment</td>
                  <td className="py-1.5 px-3.5 text-right text-emerald-700 font-sans font-bold">-${downPayment.toLocaleString()}</td>
                </tr>
              )}
              {customerRebate > 0 && (
                <tr className="bg-emerald-50/60 text-emerald-900 font-semibold text-[10.5px] sm:text-[11px]">
                  <td className="py-1.5 px-3.5 font-sans">Manufacturer / Customer Promotional Rebate</td>
                  <td className="py-1.5 px-3.5 text-right text-emerald-700 font-sans font-bold">-${customerRebate.toLocaleString()}</td>
                </tr>
              )}
              <tr
                className="deal-print-total-row font-black text-xs sm:text-sm"
                style={{ backgroundColor: '#0f172a', color: '#ffffff' }}
              >
                <td
                  className="py-2.5 px-3.5 uppercase tracking-wider font-sans"
                  style={{ backgroundColor: '#0f172a', color: '#ffffff' }}
                >
                  {paymentPreference === "CASH" ? "TOTAL CASH SETTLEMENT AMOUNT DUE" : "TOTAL AMOUNT FINANCED"}
                </td>
                <td
                  className="py-2.5 px-3.5 text-right font-sans font-black text-sm sm:text-base"
                  style={{ backgroundColor: '#0f172a', color: '#67e8f9' }}
                >
                  ${(paymentPreference === "CASH" ? Math.max(0, totalAmountDue + productTotal - tradeEquity - customerRebate) : totalFinanced).toLocaleString()}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {paymentPreference === "FINANCE" && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs bg-slate-50 border border-slate-300 p-2.5 rounded-xl text-center shadow-sm font-sans">
            <div className="border-r border-slate-200 last:border-r-0">
              <span className="text-[8px] text-slate-500 uppercase font-bold block">Lender Partner</span>
              <strong className="text-slate-900 text-[10.5px] sm:text-[11px] truncate block font-sans">Toyota Financial Services</strong>
            </div>
            <div className="border-r border-slate-200 last:border-r-0">
              <span className="text-[8px] text-slate-500 uppercase font-bold block">Term</span>
              <strong className="text-slate-900 text-[10.5px] sm:text-[11px] font-sans">{termMonths} Months</strong>
            </div>
            <div className="border-r border-slate-200 last:border-r-0">
              <span className="text-[8px] text-slate-500 uppercase font-bold block">Annual Percentage Rate</span>
              <strong className="text-slate-900 text-[10.5px] sm:text-[11px] font-sans">{apr}% APR</strong>
            </div>
            <div>
              <span className="text-[8px] text-slate-500 uppercase font-bold block">Monthly Payment</span>
              <strong className="text-cyan-800 text-xs sm:text-sm font-black font-sans">${Math.round(monthlyPayment)}/mo</strong>
            </div>
          </div>
        )}

        <div className="border border-slate-300 rounded-xl p-2.5 space-y-1.5 text-xs bg-slate-50/40 shadow-sm font-sans">
          <span className="text-[8.5px] sm:text-[9px] font-black uppercase tracking-wider text-slate-500 block border-b border-slate-200 pb-1">
            4. DOCUMENTATION &amp; COMPLIANCE VERIFICATION
          </span>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[9.5px] sm:text-[10px]">
            {["Driver's License (Front)", "Driver's License (Back)", "Proof of Income", "Insurance Card"].map((slot) => (
              <div key={slot} className="bg-white p-1.5 sm:p-2 rounded-lg border border-slate-200 flex items-center justify-between">
                <span className="text-slate-700 font-semibold truncate">{slot}</span>
                <span className="text-emerald-600 font-bold flex items-center gap-1">
                  <FaCheckCircle size={9} /> Verified
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="border-2 border-slate-900 rounded-xl p-3 space-y-2 bg-white shadow-sm font-sans">
          <span className="text-[8.5px] sm:text-[9px] font-black uppercase tracking-wider text-slate-900 block border-b border-slate-200 pb-1">
            5. ELECTRONIC SIGNATURE &amp; LEGAL CERTIFICATION
          </span>
          <p className="text-[8px] text-slate-500 leading-tight">
            The Buyer certifies under penalty of perjury that all submitted information and uploaded documentation are true, accurate, and complete. This agreement conforms with the U.S. Electronic Signatures in Global and National Commerce Act (E-SIGN).
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-0.5">
            <div className="border border-slate-300 rounded-xl p-2.5 bg-slate-50/60 flex flex-col justify-between h-24 sm:h-28 relative overflow-hidden">
              <div className="flex justify-between items-start">
                <span className="text-[8px] font-black uppercase text-slate-600 tracking-wider">Buyer Digital Signature</span>
                <span className="text-[7.5px] sm:text-[8px] text-emerald-700 bg-emerald-100 border border-emerald-300 px-1.5 py-0.5 rounded font-bold">
                  ✓ Verified E-Sign
                </span>
              </div>

              {signature.dataUrl ? (
                <div className="h-10 sm:h-12 flex items-center justify-center overflow-hidden my-1">
                  <img
                    src={signature.dataUrl}
                    alt="Customer Signature"
                    className="max-h-full max-w-full object-contain"
                  />
                </div>
              ) : (
                <div className="my-auto text-center py-1">
                  <span className="font-serif italic font-extrabold text-base sm:text-lg text-slate-900 block tracking-wide" style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}>
                    {customerName}
                  </span>
                  <div className="w-3/4 mx-auto border-b border-slate-400 border-dashed mt-0.5" />
                </div>
              )}

              <div className="flex justify-between items-center text-[7.5px] sm:text-[8px] text-slate-500 border-t border-slate-200 pt-1">
                <span>Signer: <strong>{signature.signedBy || customerName}</strong></span>
                <span>Signed: {signature.signedAt ? new Date(signature.signedAt).toLocaleDateString() : new Date().toLocaleDateString()}</span>
              </div>
            </div>

            <div className="border border-slate-300 rounded-xl p-2.5 bg-slate-50/60 flex flex-col justify-between h-24 sm:h-28 relative overflow-hidden">
              <div className="flex justify-between items-start">
                <span className="text-[8px] font-black uppercase text-slate-600 tracking-wider">Authorized Dealership Approval</span>
                <span className="text-[7.5px] sm:text-[8px] text-emerald-700 bg-emerald-100 border border-emerald-300 px-1.5 py-0.5 rounded font-bold">
                  ✓ Approved &amp; Funded
                </span>
              </div>

              <div className="my-auto text-center py-1">
                <span className="font-serif italic font-extrabold text-base sm:text-lg text-slate-900 block tracking-wide" style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}>
                  {formatCleanSalespersonName(salesConsultantName)}
                </span>
                <div className="w-3/4 mx-auto border-b border-slate-400 border-dashed mt-0.5" />
                <span className="text-[7.5px] sm:text-[8px] text-slate-500 uppercase tracking-widest block mt-0.5">Authorized Dealership Officer</span>
              </div>

              <div className="flex justify-between items-center text-[7.5px] sm:text-[8px] text-slate-500 border-t border-slate-200 pt-1">
                <span>Approved: {approval.approvedAt ? new Date(approval.approvedAt).toLocaleDateString() : new Date().toLocaleDateString()}</span>
                <span>Status: <strong>CONTRACT APPROVED</strong></span>
              </div>
            </div>
          </div>
        </div>

        <div className="pt-2 flex justify-between items-center text-[7.5px] sm:text-[8px] text-slate-400 border-t border-slate-200 font-sans">
          <span>System Generated by {dealershipTitle} AI Deal Studio · 256-Bit Encrypted Record</span>
          <span>Page 1 of 1 · Deal Reference #{deal?.dealNumber || "D493896"}</span>
        </div>
      </div>

      <style dangerouslySetInnerHTML={{
        __html: `
        @media print {
          @page {
            size: letter portrait;
            margin: 6mm;
          }
          html, body {
            background: #ffffff !important;
            color: #0f172a !important;
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          body * {
            visibility: hidden !important;
          }
          #deal-print-document, #deal-print-document * {
            visibility: visible !important;
          }
          #deal-print-document {
            position: fixed !important;
            left: 0 !important;
            top: 0 !important;
            width: 100% !important;
            max-width: 100% !important;
            margin: 0 !important;
            padding: 10px !important;
            border: none !important;
            box-shadow: none !important;
            background: #ffffff !important;
            color: #0f172a !important;
            page-break-after: avoid !important;
            page-break-inside: avoid !important;
          }
          .print\\:hidden, button {
            display: none !important;
          }
        }
      `}} />
    </div>
  );
};

export default DealPrintReport;
